// Iter 82.4 / Phase E2.0 — Attendance Policies admin.
// Specificity-aware resolution mirrors leave policies. Each policy
// owns: late & early thresholds, consecutive-warning / auto-LOP
// triggers, regularization window + caps, idle-time threshold (E2.1
// aggregator), and an exclusive `is_default` slot.
import { useCallback, useEffect, useState } from "react";
import { Plus, PencilSimple, Trash, FloppyDisk, X } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { toast } from "sonner";

const EMPTY_POLICY = {
    code: "", name: "",
    late_threshold_minutes: 15, early_leave_threshold_minutes: 15,
    late_consecutive_warning_at: 3, auto_lop_on_consecutive_absents: 3,
    regularization_allowed_days_back: 30, regularization_max_days_per_month: 5,
    regularization_requires_evidence: false,
    idle_tracking_enabled: false, idle_threshold_minutes: 10,
    // Iter 98 / Phase E2.4 — Desktop Agent → Attendance Bridge config.
    attendance_source_mode: "manual",
    min_active_minutes_for_checkin: 5,
    auto_checkout_offline_threshold_minutes: 30,
    offline_gap_threshold_minutes: 30,
    idle_treatment: "cap",
    locked_treatment: "deduct",
    idle_cap_pct: 20.0,
    locked_cap_pct: 20.0,
    applies_to_employee_ids: [], applies_to_designations: [],
    applies_to_departments: [], applies_to_employment_categories: [],
    is_default: false, is_active: true,
};

export default function AttendancePolicies() {
    const { can } = useAuth();
    const [policies, setPolicies] = useState([]);
    const [hrMeta, setHrMeta] = useState({ departments: [], designations: [], employment_categories: [] });
    const [draft, setDraft] = useState(EMPTY_POLICY);
    const [editRow, setEditRow] = useState(null);
    const [open, setOpen] = useState(false);

    const reload = useCallback(async () => {
        try {
            const [p, hm] = await Promise.all([
                api.get("/attendance/policy/list"),
                api.get("/hr/employee/meta"),
            ]);
            setPolicies(p.data.items || []);
            setHrMeta(hm.data || {});
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load policies"));
        }
    }, []);

    useEffect(() => { Promise.resolve().then(() => { reload(); }); }, [reload]);

    const openCreate = () => { setEditRow(null); setDraft(EMPTY_POLICY); setOpen(true); };
    const openEdit = (row) => { setEditRow(row); setDraft({ ...EMPTY_POLICY, ...row }); setOpen(true); };
    const close = () => { setOpen(false); setEditRow(null); setDraft(EMPTY_POLICY); };

    const save = async () => {
        try {
            const body = { ...draft, code: (draft.code || "").trim() };
            if (editRow) {
                await api.patch(`/attendance/policy/${editRow.id}`, body);
                toast.success("Policy updated.");
            } else {
                await api.post("/attendance/policy", body);
                toast.success("Policy created.");
            }
            close();
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Save failed"));
        }
    };

    const remove = async (row) => {
        if (!window.confirm(`Delete policy ${row.code} — ${row.name}?`)) return;
        try {
            await api.delete(`/attendance/policy/${row.id}`);
            toast.success("Deleted.");
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Delete failed"));
        }
    };

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1400px]" data-testid="attendance-policies-page">
            <PageHeader
                eyebrow="Attendance Configuration · E2.0"
                title="Attendance Policies"
                description="Specificity-driven resolution: Employee → Designation → Department → Employment Category → Default. Policies govern late / early thresholds, regularization caps, and idle classification."
                actions={can("employee:admin") && (
                    <Button onClick={openCreate} data-testid="policy-new-btn"><Plus size={14} className="mr-1" /> New Policy</Button>
                )}
            />

            <section className="mt-6 bg-white rounded-xl border border-slate-200 p-5" data-testid="policies-section">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600 text-xs">
                        <tr>
                            <th className="text-left px-3 py-2 font-medium">Code</th>
                            <th className="text-left px-3 py-2 font-medium">Name</th>
                            <th className="text-left px-3 py-2 font-medium">Late / Early</th>
                            <th className="text-left px-3 py-2 font-medium">Regularization</th>
                            <th className="text-left px-3 py-2 font-medium">Scope</th>
                            <th className="text-left px-3 py-2 font-medium">Flags</th>
                            <th className="w-24" />
                        </tr>
                    </thead>
                    <tbody>
                        {policies.length === 0 && (
                            <tr><td colSpan={7} className="py-8 text-center text-slate-400">No policies yet. Create at least one default to enable E2.1 attendance classification.</td></tr>
                        )}
                        {policies.map((p) => {
                            const scope = [];
                            if (p.applies_to_employee_ids?.length) scope.push(`${p.applies_to_employee_ids.length} employee(s)`);
                            if (p.applies_to_designations?.length) scope.push(`${p.applies_to_designations.length} desig`);
                            if (p.applies_to_departments?.length) scope.push(`${p.applies_to_departments.length} dept`);
                            if (p.applies_to_employment_categories?.length) scope.push(`${p.applies_to_employment_categories.length} cat`);
                            if (scope.length === 0) scope.push(p.is_default ? "Default" : "Unscoped");
                            return (
                                <tr key={p.id} className="border-t border-slate-100" data-testid={`policy-row-${p.id}`}>
                                    <td className="px-3 py-2 font-mono text-xs">{p.code}</td>
                                    <td className="px-3 py-2 text-slate-700">{p.name}</td>
                                    <td className="px-3 py-2 text-xs text-slate-600">{p.late_threshold_minutes}m / {p.early_leave_threshold_minutes}m</td>
                                    <td className="px-3 py-2 text-xs text-slate-600">{p.regularization_max_days_per_month}/mo · {p.regularization_allowed_days_back}d back</td>
                                    <td className="px-3 py-2 text-xs text-slate-600">{scope.join(", ")}</td>
                                    <td className="px-3 py-2 text-xs">
                                        <div className="flex flex-wrap gap-1">
                                            {p.is_default && <Badge className="bg-emerald-100 text-emerald-700">Default</Badge>}
                                            {p.idle_tracking_enabled && <Badge className="bg-amber-100 text-amber-700">Idle {p.idle_threshold_minutes}m</Badge>}
                                            {p.regularization_requires_evidence && <Badge className="bg-sky-100 text-sky-700">Evidence</Badge>}
                                            {!p.is_active && <Badge variant="secondary">Inactive</Badge>}
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        {can("employee:admin") && (
                                            <>
                                                <button onClick={() => openEdit(p)} className="text-slate-500 hover:text-slate-700 mr-2" data-testid={`policy-edit-${p.id}`}><PencilSimple size={14} /></button>
                                                <button onClick={() => remove(p)} className="text-rose-500 hover:text-rose-700" data-testid={`policy-delete-${p.id}`}><Trash size={14} /></button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </section>

            <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
                <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="policy-dialog">
                    <DialogHeader>
                        <DialogTitle>{editRow ? "Edit Attendance Policy" : "New Attendance Policy"}</DialogTitle>
                        <DialogDescription>Configure thresholds, regularization caps, idle classifier, and scope. Resolution follows specificity (most-specific wins).</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label className="text-xs">Code</Label><Input data-testid="policy-code" value={draft.code || ""} onChange={(e) => setDraft({ ...draft, code: e.target.value })} placeholder="STD-ATP" /></div>
                            <div><Label className="text-xs">Name</Label><Input data-testid="policy-name" value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Standard Attendance Policy" /></div>
                        </div>

                        <fieldset className="border border-slate-200 rounded-md p-3 space-y-2">
                            <legend className="text-xs font-semibold px-1">Late / Early Thresholds</legend>
                            <div className="grid grid-cols-2 gap-3">
                                <div><Label className="text-xs">Late threshold (min beyond shift start)</Label><Input type="number" data-testid="policy-late" value={draft.late_threshold_minutes} onChange={(e) => setDraft({ ...draft, late_threshold_minutes: parseInt(e.target.value || "0", 10) })} /></div>
                                <div><Label className="text-xs">Early departure (min before shift end)</Label><Input type="number" data-testid="policy-early" value={draft.early_leave_threshold_minutes} onChange={(e) => setDraft({ ...draft, early_leave_threshold_minutes: parseInt(e.target.value || "0", 10) })} /></div>
                                <div><Label className="text-xs">Consecutive lates → warning</Label><Input type="number" value={draft.late_consecutive_warning_at} onChange={(e) => setDraft({ ...draft, late_consecutive_warning_at: parseInt(e.target.value || "0", 10) })} /></div>
                                <div><Label className="text-xs">Consecutive absents → auto-LOP</Label><Input type="number" value={draft.auto_lop_on_consecutive_absents} onChange={(e) => setDraft({ ...draft, auto_lop_on_consecutive_absents: parseInt(e.target.value || "0", 10) })} /></div>
                            </div>
                        </fieldset>

                        <fieldset className="border border-slate-200 rounded-md p-3 space-y-2">
                            <legend className="text-xs font-semibold px-1">Regularization Window</legend>
                            <div className="grid grid-cols-2 gap-3">
                                <div><Label className="text-xs">Allowed days back</Label><Input type="number" data-testid="policy-reg-back" value={draft.regularization_allowed_days_back} onChange={(e) => setDraft({ ...draft, regularization_allowed_days_back: parseInt(e.target.value || "0", 10) })} /></div>
                                <div><Label className="text-xs">Max regularizations / month</Label><Input type="number" data-testid="policy-reg-max" value={draft.regularization_max_days_per_month} onChange={(e) => setDraft({ ...draft, regularization_max_days_per_month: parseInt(e.target.value || "0", 10) })} /></div>
                            </div>
                            <label className="flex items-center gap-2 text-xs"><input type="checkbox" data-testid="policy-evidence" checked={!!draft.regularization_requires_evidence} onChange={(e) => setDraft({ ...draft, regularization_requires_evidence: e.target.checked })} /> Require supporting evidence</label>
                        </fieldset>

                        <fieldset className="border border-slate-200 rounded-md p-3 space-y-2">
                            <legend className="text-xs font-semibold px-1">Idle Tracking (E2.1 classifier)</legend>
                            <label className="flex items-center gap-2 text-xs"><input type="checkbox" data-testid="policy-idle" checked={!!draft.idle_tracking_enabled} onChange={(e) => setDraft({ ...draft, idle_tracking_enabled: e.target.checked })} /> Enable idle / away detection</label>
                            <div><Label className="text-xs">Idle threshold (minutes of zero activity)</Label><Input type="number" data-testid="policy-idle-threshold" value={draft.idle_threshold_minutes} onChange={(e) => setDraft({ ...draft, idle_threshold_minutes: parseInt(e.target.value || "0", 10) })} /></div>
                        </fieldset>

                        <fieldset className="border border-violet-200 rounded-md p-3 space-y-2 bg-violet-50/30">
                            <legend className="text-xs font-semibold px-1 text-violet-800">Desktop Agent — Attendance Bridge (Iter 98 / Phase E2.4)</legend>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-xs">Attendance source mode</Label>
                                    <select className="w-full border rounded-md text-sm h-9 px-2"
                                            data-testid="policy-source-mode"
                                            value={draft.attendance_source_mode || "manual"}
                                            onChange={(e) => setDraft({ ...draft, attendance_source_mode: e.target.value })}>
                                        <option value="manual">Manual ESS (web / biometric)</option>
                                        <option value="desktop_agent">Desktop Agent (WFH)</option>
                                        <option value="hybrid">Hybrid (agent + manual fallback)</option>
                                    </select>
                                    <div className="text-[10px] text-slate-500 mt-1">Determines whether the bridge synthesises check-in / check-out from agent heartbeats. Per-employee override on the Employee record takes precedence.</div>
                                </div>
                                <div>
                                    <Label className="text-xs">Min active min for auto check-in</Label>
                                    <Input type="number" data-testid="policy-min-checkin"
                                           value={draft.min_active_minutes_for_checkin ?? 5}
                                           onChange={(e) => setDraft({ ...draft, min_active_minutes_for_checkin: parseInt(e.target.value || "0", 10) })} />
                                </div>
                                <div>
                                    <Label className="text-xs">Auto-checkout offline threshold (min)</Label>
                                    <Input type="number" data-testid="policy-auto-checkout"
                                           value={draft.auto_checkout_offline_threshold_minutes ?? 30}
                                           onChange={(e) => setDraft({ ...draft, auto_checkout_offline_threshold_minutes: parseInt(e.target.value || "0", 10) })} />
                                    <div className="text-[10px] text-slate-500 mt-1">Capped at shift_end + grace.</div>
                                </div>
                                <div>
                                    <Label className="text-xs">Offline gap threshold (min)</Label>
                                    <Input type="number" data-testid="policy-gap-threshold"
                                           value={draft.offline_gap_threshold_minutes ?? 30}
                                           onChange={(e) => setDraft({ ...draft, offline_gap_threshold_minutes: parseInt(e.target.value || "0", 10) })} />
                                </div>
                                <div>
                                    <Label className="text-xs">Idle treatment</Label>
                                    <select className="w-full border rounded-md text-sm h-9 px-2"
                                            data-testid="policy-idle-treatment"
                                            value={draft.idle_treatment || "cap"}
                                            onChange={(e) => setDraft({ ...draft, idle_treatment: e.target.value })}>
                                        <option value="deduct">Deduct (full)</option>
                                        <option value="ignore">Ignore</option>
                                        <option value="cap">Cap (deduct only over %)</option>
                                    </select>
                                </div>
                                <div>
                                    <Label className="text-xs">Idle cap % (when treatment = cap)</Label>
                                    <Input type="number" step="0.1"
                                           data-testid="policy-idle-cap"
                                           value={draft.idle_cap_pct ?? 20}
                                           onChange={(e) => setDraft({ ...draft, idle_cap_pct: parseFloat(e.target.value || "0") })} />
                                </div>
                                <div>
                                    <Label className="text-xs">Locked treatment</Label>
                                    <select className="w-full border rounded-md text-sm h-9 px-2"
                                            data-testid="policy-locked-treatment"
                                            value={draft.locked_treatment || "deduct"}
                                            onChange={(e) => setDraft({ ...draft, locked_treatment: e.target.value })}>
                                        <option value="deduct">Deduct (full)</option>
                                        <option value="ignore">Ignore</option>
                                        <option value="cap">Cap (deduct only over %)</option>
                                    </select>
                                </div>
                                <div>
                                    <Label className="text-xs">Locked cap % (when treatment = cap)</Label>
                                    <Input type="number" step="0.1"
                                           data-testid="policy-locked-cap"
                                           value={draft.locked_cap_pct ?? 20}
                                           onChange={(e) => setDraft({ ...draft, locked_cap_pct: parseFloat(e.target.value || "0") })} />
                                </div>
                            </div>
                        </fieldset>

                        <fieldset className="border border-slate-200 rounded-md p-3 space-y-2">
                            <legend className="text-xs font-semibold px-1">Scope (specificity: Employee → Designation → Department → Category → Default)</legend>
                            <div><Label className="text-xs">Departments</Label>
                                <Chips options={(hrMeta.departments || []).map((d) => d.name)} value={draft.applies_to_departments || []} onChange={(v) => setDraft({ ...draft, applies_to_departments: v })} dataTestId="policy-deps" />
                            </div>
                            <div><Label className="text-xs">Designations</Label>
                                <Chips options={(hrMeta.designations || []).map((d) => d.name)} value={draft.applies_to_designations || []} onChange={(v) => setDraft({ ...draft, applies_to_designations: v })} dataTestId="policy-desigs" />
                            </div>
                            <div><Label className="text-xs">Employment Categories</Label>
                                <Chips options={hrMeta.employment_categories || []} value={draft.applies_to_employment_categories || []} onChange={(v) => setDraft({ ...draft, applies_to_employment_categories: v })} dataTestId="policy-cats" />
                            </div>
                        </fieldset>

                        <div className="flex flex-col gap-2">
                            <label className="flex items-center gap-2 text-xs"><input type="checkbox" data-testid="policy-default" checked={!!draft.is_default} onChange={(e) => setDraft({ ...draft, is_default: e.target.checked })} /> Mark as Default (only one)</label>
                            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={draft.is_active !== false} onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })} /> Active</label>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={close}><X size={14} className="mr-1" /> Cancel</Button>
                        <Button onClick={save} data-testid="policy-save"><FloppyDisk size={14} className="mr-1" /> Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Chips({ options, value, onChange, dataTestId }) {
    const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
    return (
        <div className="flex flex-wrap gap-1.5 border border-slate-200 rounded-md p-2 bg-white" data-testid={dataTestId}>
            {options.length === 0 && <span className="text-xs text-slate-400">No options available</span>}
            {options.map((o) => (
                <button key={o} type="button" onClick={() => toggle(o)} className={`text-xs px-2 py-1 rounded-full border ${value.includes(o) ? "bg-indigo-100 border-indigo-300 text-indigo-700" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                    {o}
                </button>
            ))}
        </div>
    );
}
